import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  KeyRound, Loader2, CheckCircle2, AlertOctagon, Trash2,
  Eye, EyeOff, RefreshCw, ShieldAlert, Download, Database, ExternalLink,
} from "lucide-react";

/**
 * Wordfence Master — Intelligence Vulnerability Data Feed API token +
 * cache sync + browse-cache table.
 *
 * The Wordfence Intelligence v3 feed returns CVE-style vulnerability
 * data for WordPress plugins / themes / core. It's a global feed —
 * per-client matching happens on the Client Wordfence tab where the
 * operator registers each client's stack (plugins + versions).
 */
export default function WordfenceMasterPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyDraft, setKeyDraft] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(null);
  const [vulns, setVulns] = useState([]);
  const [vulnFilter, setVulnFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wordfence/master");
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Wordfence state");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadVulns = useCallback(async () => {
    try {
      const { data } = await api.get("/wordfence/vulnerabilities?limit=100");
      setVulns(data.vulnerabilities || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load vulnerability cache");
    }
  }, []);
  useEffect(() => { if (state?.vulnerabilities_cached > 0) loadVulns(); },
    [state?.vulnerabilities_cached, loadVulns]);

  const saveKey = async () => {
    if (!keyDraft.trim()) { toast.error("Paste an API token first"); return; }
    setBusy("save");
    try {
      const { data } = await api.put("/wordfence/master",
        { api_token: keyDraft.trim() });
      toast.success("Wordfence token saved (encrypted at rest)");
      setKeyDraft(""); setShowKey(false);
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the Wordfence master API token? Vulnerability feed syncs will stop.")) return;
    setBusy("clear");
    try {
      await api.delete("/wordfence/master");
      toast.success("Cleared");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(null); }
  };

  const verify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.post("/wordfence/master/verify");
      if (data.ok) toast.success(`Wordfence: ${data.detail}`);
      else toast.error(`Wordfence rejected: ${data.detail}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setBusy(null); }
  };

  const pollingRef = React.useRef(false);
  const pollSync = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        let data;
        try {
          ({ data } = await api.get("/wordfence/master"));
        } catch { continue; }
        setState(data);
        if (!data.sync_running) {
          if (data.last_sync_error) {
            toast.error(`Wordfence sync failed: ${data.last_sync_error}`);
          } else {
            toast.success(`Synced ${(data.last_sync_count || 0).toLocaleString()} vulnerability record(s)`);
          }
          await loadVulns();
          return;
        }
      }
    } finally { pollingRef.current = false; }
  }, [loadVulns]);

  useEffect(() => {
    if (state?.sync_running) pollSync();
  }, [state?.sync_running, pollSync]);

  const syncNow = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/wordfence/master/sync");
      if (data.already_running) {
        toast.info("A sync is already running — the feed will refresh when it finishes");
      } else {
        toast.info("Sync started — downloading the full vulnerability feed in the background…");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setBusy(null); }
  };

  const configured = state?.configured;
  const syncing = Boolean(state?.sync_running);

  const filteredVulns = vulns.filter((v) => {
    if (!vulnFilter.trim()) return true;
    const q = vulnFilter.toLowerCase();
    return (v.title || "").toLowerCase().includes(q)
      || (v.cve || "").toLowerCase().includes(q)
      || (v.software_slugs || []).some((s) => s.toLowerCase().includes(q));
  }).slice(0, 50);

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="wordfence-master-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#B22222]/10 grid place-items-center">
          <ShieldAlert className="h-5 w-5 text-[#B22222]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            Wordfence Master
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Agency-wide Wordfence Intelligence API token + local vulnerability
            cache. The per-client Wordfence tab uses this cache to flag
            vulnerable plugins/themes, and receives real-time scan / firewall
            events via a unique webhook per client.
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">API token</div>
            {loading ? (
              <Badge variant="outline"
                className="ml-2 rounded-sm text-[10px] font-mono">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> loading
              </Badge>
            ) : configured ? (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-emerald-100 text-emerald-800 border-emerald-200"
                data-testid="wordfence-master-status-configured">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Configured
                {state?.key_hint && (
                  <span className="ml-1 opacity-70">· {state.key_hint}</span>
                )}
              </Badge>
            ) : (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-amber-100 text-amber-800 border-amber-200"
                data-testid="wordfence-master-status-missing">
                <AlertOctagon className="h-3 w-3 mr-1" /> Not configured
              </Badge>
            )}
          </div>

          <div>
            <Label className="label-overline">
              {configured ? "Replace / rotate token" : "Paste your Wordfence Intelligence API token"}
            </Label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)}
                  placeholder="Generate at wordfence.com/account/integrations"
                  className="rounded-sm font-mono text-[12px] pr-8"
                  data-testid="wordfence-master-key-input" />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <Button onClick={saveKey} disabled={busy !== null || !keyDraft.trim()}
                className="rounded-sm bg-[#B22222] hover:bg-[#8B0000] text-white"
                data-testid="wordfence-master-save-btn">
                {busy === "save"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : "Save"}
              </Button>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-1.5 leading-relaxed">
              Generate a free token at{" "}
              <a href="https://www.wordfence.com/account/integrations"
                target="_blank" rel="noreferrer"
                className="text-[#B22222] hover:underline inline-flex items-center gap-0.5">
                wordfence.com/account/integrations
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
              . Only shown once — copy immediately. Token is Fernet-encrypted at
              rest and only ever sent to Wordfence in the ``Authorization: Bearer``
              header — never appears in URLs, logs, or browser code.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1 border-t border-zinc-100 flex-wrap">
            <Button size="sm" variant="outline" onClick={verify}
              disabled={busy !== null || !configured}
              className="rounded-sm"
              data-testid="wordfence-master-verify-btn">
              {busy === "verify"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Test connection
            </Button>
            <Button size="sm" onClick={syncNow}
              disabled={busy !== null || !configured || syncing}
              className="rounded-sm bg-[#B22222] hover:bg-[#8B0000] text-white"
              data-testid="wordfence-master-sync-btn">
              {busy === "sync" || syncing
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Download className="h-3.5 w-3.5 mr-1.5" />}
              {syncing ? "Syncing…" : "Sync feed now"}
            </Button>
            <Button size="sm" variant="outline" onClick={clearKey}
              disabled={busy !== null || !configured}
              className="rounded-sm text-red-600 hover:bg-red-50"
              data-testid="wordfence-master-clear-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear token
            </Button>
            {state?.last_verified_at && (
              <span className="ml-auto text-[10px] font-mono text-zinc-500">
                {state.last_verified_ok ? (
                  <span className="text-emerald-700">
                    <CheckCircle2 className="inline h-3 w-3 mr-0.5" />
                    Verified {new Date(state.last_verified_at).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-red-700">
                    <AlertOctagon className="inline h-3 w-3 mr-0.5" />
                    Failed {new Date(state.last_verified_at).toLocaleString()}
                    {state.last_verified_detail && (
                      <span className="ml-1 opacity-70">· {state.last_verified_detail}</span>
                    )}
                  </span>
                )}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cache stats */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Database className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">Vulnerability cache</div>
            {syncing && (
              <Badge className="rounded-sm text-[10px] font-mono bg-blue-100 text-blue-800 border-blue-200"
                data-testid="wordfence-sync-running-badge">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sync in progress…
              </Badge>
            )}
            {!syncing && state?.last_sync_error && (
              <Badge className="rounded-sm text-[10px] font-mono bg-red-100 text-red-800 border-red-200"
                data-testid="wordfence-sync-error-badge">
                <AlertOctagon className="h-3 w-3 mr-1" /> Last sync failed: {state.last_sync_error}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-sm border border-zinc-200 p-3">
              <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                Records cached
              </div>
              <div className="font-display font-bold text-2xl mt-0.5">
                {(state?.vulnerabilities_cached || 0).toLocaleString()}
              </div>
            </div>
            <div className="rounded-sm border border-zinc-200 p-3">
              <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                Last sync
              </div>
              <div className="font-display font-bold text-sm mt-0.5">
                {state?.last_sync_at
                  ? new Date(state.last_sync_at).toLocaleString()
                  : "—"}
              </div>
              {state?.last_sync_count != null && (
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  {state.last_sync_count.toLocaleString()} records
                </div>
              )}
            </div>
            <div className="rounded-sm border border-zinc-200 p-3">
              <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                Scheduled sync
              </div>
              <div className="font-display font-bold text-sm mt-0.5">Daily · 03:45</div>
              <a href="/scheduler" className="text-[10px] text-[#B22222] hover:underline">
                Re-time on /scheduler →
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vulnerability browse (top 50 by CVSS after filtering) */}
      {(state?.vulnerabilities_cached || 0) > 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="label-overline">Cache browser · top 50 by CVSS</div>
              <Input value={vulnFilter}
                onChange={(e) => setVulnFilter(e.target.value)}
                placeholder="Filter by CVE, plugin slug, or title…"
                className="rounded-sm text-[12px] w-72"
                data-testid="wordfence-master-vuln-filter" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left border-b border-zinc-200">
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">CVSS</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Software</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Title</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">CVE</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Published</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVulns.map((v) => (
                    <tr key={v.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-1.5">
                        <Badge className={`rounded-sm text-[9px] font-mono ${
                          v.cvss_rating === "Critical" ? "bg-red-100 text-red-800"
                          : v.cvss_rating === "High" ? "bg-orange-100 text-orange-800"
                          : v.cvss_rating === "Medium" ? "bg-amber-100 text-amber-800"
                          : "bg-zinc-100 text-zinc-700"}`}>
                          {v.cvss_score ?? "—"}
                        </Badge>
                      </td>
                      <td className="py-1.5 font-mono">
                        {(v.software_slugs || []).slice(0, 2).join(", ")}
                        {(v.software_slugs || []).length > 2 && ` +${v.software_slugs.length - 2}`}
                      </td>
                      <td className="py-1.5 max-w-md truncate">{v.title}</td>
                      <td className="py-1.5 font-mono text-[10px]">
                        {v.cve_link ? (
                          <a href={v.cve_link} target="_blank" rel="noreferrer"
                            className="text-[#B22222] hover:underline">
                            {v.cve || "—"}
                          </a>
                        ) : (v.cve || "—")}
                      </td>
                      <td className="py-1.5 font-mono text-zinc-500">
                        {v.published ? (v.published + "").slice(0, 10) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredVulns.length === 0 && (
                <div className="text-center py-4 text-[11px] text-zinc-500">
                  No matches for &quot;{vulnFilter}&quot;.
                </div>
              )}
            </div>
            <div className="text-[9px] font-mono text-zinc-400 mt-3 leading-relaxed">
              Contains vulnerability data © Defiant Inc + MITRE Corporation.
              Full copyright notices attached to each record — see the
              Wordfence Intelligence terms:{" "}
              <a href="https://www.wordfence.com/wordfence-intelligence-terms-and-conditions/"
                target="_blank" rel="noreferrer"
                className="text-[#B22222] hover:underline">
                wordfence-intelligence-terms-and-conditions
              </a>.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
